// REACT IMPORTS
import React, { useEffect, useMemo, useRef, useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// API
import { postAPICall } from "../../../config/apiCalls";

// MUI COMPONENTS
import {
	Alert,
	Autocomplete,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	MenuItem,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
} from "@mui/material";

// MUI ICONS
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

// CONSTANTS
import { plural } from "./productLibraryUtils";
import TanStackTablePagination from "../../Misc/Table/TanStackTablePagination/TanStackTablePagination";
import usePagedRows from "./usePagedRows";

const NO_PHASE = "__none__";

const MISSING_FILTERS = [
	{ value: "", label: "All" },
	{ value: "either", label: "Missing service or component" },
	{ value: "service", label: "Missing service" },
	{ value: "component", label: "Missing component" },
	{ value: "both", label: "Missing both" },
];

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// One compact checkbox row in a tag list
const tagOptionRenderer =
	(getOptionLabel) =>
	(props, option, { selected }) => {
		const { key, ...optionProps } = props;
		return (
			<li
				key={key}
				{...optionProps}
				className={`${optionProps.className} bulkTagOption`}
			>
				<Checkbox
					size="small"
					icon={checkboxIcon}
					checkedIcon={checkedIcon}
					checked={selected}
					className="bulkTagOptionCheckbox"
				/>
				{getOptionLabel(option)}
			</li>
		);
	};

const asLabel = (v) => v;

// Phases are stored by value ("Bid_Assistance") and shown by label
const phaseLabeler = (phases) => (v) =>
	phases.find((p) => p.value === v)?.label || v;

// Tag lists size to their longest option instead of the field, so options
// like "Interior - Crawl Space" stay on one line
const tagListPopper = {
	popper: {
		style: { width: "max-content", minWidth: 220 },
		placement: "bottom-start",
	},
};

// Multiple-choice picker with checkboxes, compact for the action bar
const TagPicker = ({
	label,
	options,
	value,
	onChange,
	getOptionLabel = asLabel,
	width = 220,
}) => (
	<Autocomplete
		multiple
		size="small"
		disableCloseOnSelect
		limitTags={2}
		options={options}
		value={value}
		onChange={(_, v) => onChange(v)}
		getOptionLabel={getOptionLabel}
		renderOption={tagOptionRenderer(getOptionLabel)}
		componentsProps={tagListPopper}
		renderInput={(params) => (
			<TextField
				{...params}
				label={label}
				placeholder={value.length ? "" : "Choose"}
				InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
			/>
		)}
		sx={{ width }}
	/>
);

const sameTags = (a, b) => [...a].sort().join(";") === [...b].sort().join(";");

const TagChips = ({ values, getOptionLabel }) =>
	values.length ? (
		values.map((v) => (
			<Chip
				key={v}
				label={getOptionLabel(v)}
				size="small"
				variant="outlined"
			/>
		))
	) : (
		<span className="bulkTagEmpty">Add</span>
	);

/**
 * A row's Phase, Service or Component cell, which is its own picker: click it
 * and the checkbox list opens in the cell; clicking away (or Esc) saves.
 * Shows plain chips until clicked, so hundreds of rows stay fast.
 * @param {string[]} props.values - the product's current tags
 * @param {string[]} props.options
 * @param {Function} props.getOptionLabel - (value) => label, for phases
 * @param {Function} props.onSave - async (values) => true if saved
 */
const InlineTagCell = ({
	values,
	options,
	getOptionLabel = asLabel,
	onSave,
}) => {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(values);
	const [saving, setSaving] = useState(false);

	const finish = async () => {
		setEditing(false);
		if (sameTags(draft, values)) return;
		setSaving(true);
		const saved = await onSave(draft);
		setSaving(false);
		if (!saved) setDraft(values);
	};

	if (!editing) {
		return (
			<button
				type="button"
				className="bulkTagCell"
				disabled={saving}
				onClick={() => {
					setDraft(values);
					setEditing(true);
				}}
			>
				<TagChips
					values={saving ? draft : values}
					getOptionLabel={getOptionLabel}
				/>
				{saving && <CircularProgress size={14} />}
			</button>
		);
	}

	return (
		<Autocomplete
			multiple
			size="small"
			open
			disableCloseOnSelect
			options={options}
			value={draft}
			onChange={(_, v) => setDraft(v)}
			onClose={(_, reason) => {
				if (reason !== "selectOption" && reason !== "removeOption")
					finish();
			}}
			getOptionLabel={getOptionLabel}
			renderOption={tagOptionRenderer(getOptionLabel)}
			componentsProps={tagListPopper}
			renderInput={(params) => <TextField {...params} autoFocus />}
			className="bulkTagInlinePicker"
		/>
	);
};

/**
 * Bulk tagging: select many products and add or remove Phase / Service /
 * Component tags in one save, with a preview first. A row's tags can also be
 * edited in place.
 * @param {Array} props.products - every Product Library product row
 * @param {Object} props.options - { phases, services, components }
 * @param {Function} props.onUpdated - (products, missingIds) to swap rows from HubSpot into the page and drop deleted ones
 * @param {Function} props.onMessage - (text) for the page's snackbar
 */
const BulkTagView = ({ products, options, onUpdated, onMessage }) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// FILTERS
	const [search, setSearch] = useState("");
	const [phase, setPhase] = useState("");
	const [missing, setMissing] = useState("");
	const [includeHidden, setIncludeHidden] = useState(true);

	// SELECTION
	const [selected, setSelected] = useState(() => new Set());
	const [lastClicked, setLastClicked] = useState(null);

	// Row positions change with the filters, so a shift-click range restarts
	useEffect(() => {
		setLastClicked(null);
	}, [search, phase, missing, includeHidden]);

	// BULK ACTION
	const [mode, setMode] = useState("add");
	const [phases, setPhases] = useState([]);
	const [services, setServices] = useState([]);
	const [components, setComponents] = useState([]);
	const [preview, setPreview] = useState(null); // { plan, unchangedCount, missingIds, request, mode }
	const [isWorking, setIsWorking] = useState(false);
	const [errorText, setErrorText] = useState("");

	// Every active product by default, including ones hidden from the editor
	// (QBO, adjustments); retired products live on the Inactive tab. Newest
	// updated first, like the main table, so new products are at the top.
	// The order is set once, so a row saved here doesn't jump to the top (off
	// the page you're on, and out from under a shift-click range). Products
	// new since then go first.
	const orderRef = useRef(null);
	if (!orderRef.current)
		orderRef.current = new Map(
			[...products]
				.sort((a, b) =>
					String(b.modified || "").localeCompare(
						String(a.modified || ""),
					),
				)
				.map((p, i) => [p.id, i]),
		);
	const visible = useMemo(() => {
		const q = search.trim().toLowerCase();
		const rank = (p) => orderRef.current.get(p.id) ?? -1;
		const byUpdated = [...products].sort((a, b) => rank(a) - rank(b));
		return byUpdated.filter((p) => {
			if (!p.active) return false;
			if (!includeHidden && p.hiddenReason) return false;
			if (
				q &&
				!`${p.text} ${p.description || ""}`.toLowerCase().includes(q)
			)
				return false;
			if (
				phase === NO_PHASE
					? p.phaseNames.length
					: phase && !p.phaseNames.includes(phase)
			)
				return false;
			const noService = !p.services.length;
			const noComponent = !p.components.length;
			if (missing === "service" && !noService) return false;
			if (missing === "component" && !noComponent) return false;
			if (missing === "either" && !(noService || noComponent))
				return false;
			if (missing === "both" && !(noService && noComponent)) return false;
			return true;
		});
	}, [products, search, phase, missing, includeHidden]);

	const visibleIds = visible.map((p) => p.id);
	const selectedVisible = visibleIds.filter((id) => selected.has(id)).length;
	const allSelected =
		visible.length > 0 && selectedVisible === visible.length;

	// 25 a page like the site's other tables. Selection (incl. select all
	// and shift-click ranges) works across every page of what's filtered.
	const {
		pageRows,
		pageStart,
		table: pager,
		pagination,
	} = usePagedRows(
		visible,
		[search, phase, missing, includeHidden].join("|"),
	);

	// HANDLERS
	const toggleAll = () =>
		setSelected((s) => {
			const next = new Set(s);
			if (allSelected) visibleIds.forEach((id) => next.delete(id));
			else visibleIds.forEach((id) => next.add(id));
			return next;
		});

	// Shift-click selects (or clears) the range from the last row clicked
	const toggleRow = (id, index, shiftKey) => {
		setSelected((s) => {
			const next = new Set(s);
			const on = !s.has(id);
			if (shiftKey && lastClicked !== null) {
				const [a, b] = [
					Math.min(lastClicked, index),
					Math.max(lastClicked, index),
				];
				visibleIds
					.slice(a, b + 1)
					.forEach((rid) => (on ? next.add(rid) : next.delete(rid)));
			} else if (on) next.add(id);
			else next.delete(id);
			return next;
		});
		setLastClicked(index);
	};

	// Only products still active on the page (one retired from the Active tab
	// can still be selected here)
	const selectedIds = () =>
		products.filter((p) => p.active && selected.has(p.id)).map((p) => p.id);

	const buildRequest = () => {
		const tags = { phases, services, components };
		return {
			productIds: selectedIds(),
			add: mode === "add" ? tags : {},
			remove: mode === "remove" ? tags : {},
		};
	};

	const hasAction =
		phases.length > 0 || services.length > 0 || components.length > 0;

	// The server sends back every selected product as HubSpot has it now, so
	// the page can't stay behind HubSpot (edits made there, or while the page
	// sat open). Products deleted from HubSpot leave the page and selection.
	const syncFromResponse = (data) => {
		onUpdated(data.products || [], data.missingIds || []);
		if (data.missingIds?.length)
			setSelected((s) => {
				const next = new Set(s);
				data.missingIds.forEach((id) => next.delete(id));
				return next;
			});
	};

	const postBulkTags = (body) =>
		postAPICall(
			instance,
			accounts[0],
			"/api/product-library/products/bulk-tags",
			body,
		);

	const handlePreview = async () => {
		const request = buildRequest();
		if (!request.productIds.length) {
			setErrorText("Select at least one active product.");
			return;
		}
		setIsWorking(true);
		setErrorText("");
		try {
			const res = await postBulkTags({ ...request, dryRun: true });
			syncFromResponse(res.data);
			setPreview({ ...res.data, request, mode });
		} catch (err) {
			console.error(err);
			setErrorText(
				err?.response?.data?.error || "Couldn't preview these changes.",
			);
		}
		setIsWorking(false);
	};

	const handleApply = async () => {
		setIsWorking(true);
		setErrorText("");
		try {
			const res = await postBulkTags({
				...preview.request,
				dryRun: false,
			});
			syncFromResponse(res.data);
			onMessage(
				res.data.plan.length
					? `Saved to HubSpot: ${plural(res.data.plan.length, "product")} updated.`
					: "Nothing to save: these products already matched.",
			);
			setPreview(null);
			setSelected(new Set());
			setMode("add");
			setPhases([]);
			setServices([]);
			setComponents([]);
		} catch (err) {
			console.error(err);
			// A batch can fail partway; earlier ones did save
			setErrorText(
				`${err?.response?.data?.error || "Couldn't save these tags."} Some may have saved: click Review Changes to see what's left.`,
			);
			setPreview(null);
		}
		setIsWorking(false);
	};

	// What the request asks for, for the preview: "Add Service: Design"
	const describeRequest = ({ request, mode }) => {
		const tags = mode === "add" ? request.add : request.remove;
		const parts = [
			["Phase", (tags.phases || []).map(phaseLabel)],
			["Service", tags.services || []],
			["Component", tags.components || []],
		]
			.filter(([, values]) => values.length)
			.map(([label, values]) => `${label}: ${values.join(", ")}`);
		return `${mode === "add" ? "Add" : "Remove"} ${parts.join("; ")}`;
	};

	const phaseOptions = options?.phases || [];
	const phaseValues = phaseOptions.map((p) => p.value);
	const phaseLabel = phaseLabeler(phaseOptions);

	// Saves one row's Phase, Service or Component, from its cell. Sends only
	// what was added and removed (like bulk tagging), so if the page is behind
	// HubSpot it never undoes tags someone else added.
	const saveRowField = async (product, field, values) => {
		const key = field === "phaseNames" ? "phases" : field;
		const before = product[field];
		try {
			const res = await postBulkTags({
				productIds: [product.id],
				add: { [key]: values.filter((v) => !before.includes(v)) },
				remove: { [key]: before.filter((v) => !values.includes(v)) },
				dryRun: false,
			});
			syncFromResponse(res.data);
			return true;
		} catch (err) {
			console.error(err);
			setErrorText(
				err?.response?.data?.error || "Couldn't save this product.",
			);
			return false;
		}
	};

	// RENDER
	return (
		<div className="bulkTagView">
			<div className="bulkTagFilters">
				<TextField
					size="small"
					label="Search"
					placeholder="Name or description"
					InputLabelProps={{ shrink: true }}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					sx={{ width: 280 }}
				/>
				<TextField
					select
					size="small"
					label="Phase"
					value={phase}
					onChange={(e) => setPhase(e.target.value)}
					SelectProps={{ displayEmpty: true }}
					InputLabelProps={{ shrink: true }}
					sx={{ width: 220 }}
				>
					<MenuItem value="">All Phases</MenuItem>
					{(options?.phases || []).map((p) => (
						<MenuItem key={p.value} value={p.value}>
							{p.label}
						</MenuItem>
					))}
					<MenuItem value={NO_PHASE}>No Phase</MenuItem>
				</TextField>
				<TextField
					select
					size="small"
					label="Show"
					value={missing}
					onChange={(e) => setMissing(e.target.value)}
					SelectProps={{ displayEmpty: true }}
					InputLabelProps={{ shrink: true }}
					sx={{ width: 260 }}
				>
					{MISSING_FILTERS.map((f) => (
						<MenuItem key={f.value} value={f.value}>
							{f.label}
						</MenuItem>
					))}
				</TextField>
				<FormControlLabel
					control={
						<Checkbox
							size="small"
							checked={includeHidden}
							onChange={(e) => setIncludeHidden(e.target.checked)}
						/>
					}
					label="Include accounting items and phase fee lines"
				/>
				<div className="productLibrarySpacer" />
				<span className="productLibraryMuted">
					{plural(visible.length, "product")} shown
				</span>
			</div>

			<div
				className={`bulkTagActionBar${selected.size ? " bulkTagActionBarActive" : ""}`}
			>
				<div className="bulkTagSelectedCount">
					<strong>{selected.size}</strong> selected
					{selected.size > selectedVisible &&
						` (${selected.size - selectedVisible} not shown)`}
					{selected.size > 0 && (
						<button
							type="button"
							className="productLibraryTextButton"
							onClick={() => setSelected(new Set())}
						>
							Clear
						</button>
					)}
				</div>
				<ToggleButtonGroup
					size="small"
					exclusive
					color={mode === "remove" ? "error" : "primary"}
					value={mode}
					onChange={(_, v) => v && setMode(v)}
					sx={{ height: 40 }}
				>
					<ToggleButton value="add">Add Tags</ToggleButton>
					<ToggleButton value="remove">Remove Tags</ToggleButton>
				</ToggleButtonGroup>
				<TagPicker
					label="Phase"
					options={phaseValues}
					value={phases}
					onChange={setPhases}
					getOptionLabel={phaseLabel}
				/>
				<TagPicker
					label="Service"
					options={options?.services || []}
					value={services}
					onChange={setServices}
				/>
				<TagPicker
					label="Component"
					options={options?.components || []}
					value={components}
					onChange={setComponents}
				/>
				<Button
					variant="contained"
					disableElevation
					// Red while removing, so a leftover Remove Tags is hard to miss
					color={mode === "remove" ? "error" : "primary"}
					disabled={!selected.size || !hasAction || isWorking}
					onClick={handlePreview}
					sx={{ height: 40, ml: "auto" }}
					startIcon={
						isWorking && !preview ? (
							<CircularProgress size={16} color="inherit" />
						) : null
					}
				>
					Review Changes
				</Button>
			</div>

			{errorText && (
				<Alert severity="error" onClose={() => setErrorText("")}>
					{errorText}
				</Alert>
			)}

			<div className="bulkTagTableContainer">
				<Table size="small" stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell padding="checkbox">
								<Checkbox
									size="small"
									checked={allSelected}
									indeterminate={
										selectedVisible > 0 && !allSelected
									}
									onChange={toggleAll}
									inputProps={{
										"aria-label": "Select all shown",
									}}
								/>
							</TableCell>
							<TableCell>Name</TableCell>
							<TableCell sx={{ width: 240 }}>Phase</TableCell>
							<TableCell sx={{ width: 280 }}>Service</TableCell>
							<TableCell sx={{ width: 280 }}>Component</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{pageRows.map((p, i) => {
							const index = pageStart + i;
							return (
								<TableRow
									key={p.id}
									hover
									selected={selected.has(p.id)}
								>
									<TableCell padding="checkbox">
										<Checkbox
											size="small"
											checked={selected.has(p.id)}
											onChange={(e) =>
												toggleRow(
													p.id,
													index,
													e.nativeEvent.shiftKey,
												)
											}
										/>
									</TableCell>
									<TableCell className="bulkTagNameCell">
										<span
											className="bulkTagName"
											title={p.text}
										>
											{p.text}
										</span>
										{p.hiddenReason && (
											<span className="productLibraryMuted">
												{p.hiddenReason}
											</span>
										)}
									</TableCell>
									<TableCell className="bulkTagTagCell">
										<InlineTagCell
											values={p.phaseNames}
											options={phaseValues}
											getOptionLabel={phaseLabel}
											onSave={(v) =>
												saveRowField(p, "phaseNames", v)
											}
										/>
									</TableCell>
									<TableCell className="bulkTagTagCell">
										<InlineTagCell
											values={p.services}
											options={options?.services || []}
											onSave={(v) =>
												saveRowField(p, "services", v)
											}
										/>
									</TableCell>
									<TableCell className="bulkTagTagCell">
										<InlineTagCell
											values={p.components}
											options={options?.components || []}
											onSave={(v) =>
												saveRowField(p, "components", v)
											}
										/>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
				{!visible.length && (
					<div className="productLibraryEmpty">
						Nothing matches. Everything shown is tagged, or try
						other filters.
					</div>
				)}
			</div>
			<TanStackTablePagination table={pager} pagination={pagination} />

			{/* Preview before saving */}
			<Dialog
				open={Boolean(preview)}
				fullWidth
				maxWidth="md"
				onClose={isWorking ? undefined : () => setPreview(null)}
			>
				<DialogTitle id="editProductDialogTitle">
					Review Changes
				</DialogTitle>
				<DialogContent id="editProductDialogContent">
					{preview && (
						<>
							<div className="bulkTagPreviewRequest">
								<strong>{describeRequest(preview)}</strong> on{" "}
								{plural(
									preview.request.productIds.length,
									"selected product",
								)}
							</div>
							<div>
								{preview.plan.length
									? `${plural(preview.plan.length, "product")} will change in HubSpot.`
									: preview.mode === "add"
										? "None of the selected products would change: they all already have these tags in HubSpot."
										: "None of the selected products would change: none of them have these tags in HubSpot."}
								{preview.plan.length > 0 &&
									preview.unchangedCount > 0 &&
									` ${plural(preview.unchangedCount, "product")} already ${preview.unchangedCount === 1 ? "matches" : "match"}.`}
								{preview.missingIds?.length > 0 &&
									` ${plural(preview.missingIds.length, "product")} no longer in HubSpot, skipped and removed from the page.`}
							</div>
							{preview.plan.length > 0 && (
								<div className="bulkTagPreviewList">
									<Table size="small" stickyHeader>
										<TableHead>
											<TableRow>
												<TableCell>Name</TableCell>
												<TableCell>Change</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{preview.plan.map((p) => (
												<TableRow key={p.id}>
													<TableCell className="bulkTagPreviewName">
														{p.name}
													</TableCell>
													<TableCell>
														{p.changed.map((c) => (
															<div key={c.label}>
																<strong>
																	{c.label}:
																</strong>{" "}
																{c.from} →{" "}
																{c.to}
															</div>
														))}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</div>
							)}
							<div className="dialogButtons">
								<Button
									variant="outlined"
									color="error"
									disabled={isWorking}
									onClick={() => setPreview(null)}
								>
									Cancel
								</Button>
								<Button
									variant="contained"
									disableElevation
									disabled={isWorking || !preview.plan.length}
									onClick={handleApply}
									startIcon={
										isWorking ? (
											<CircularProgress
												size={16}
												color="inherit"
											/>
										) : null
									}
								>
									Save{" "}
									{plural(preview.plan.length, "Product")}
								</Button>
							</div>
						</>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default BulkTagView;
